import { useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    COMPANY_ADMIN_ORGANIZATION_LOCATIONS_CREATE_ROUTE,
    COMPANY_ADMIN_ROUTE,
    getEditLocationRoute,
} from "../../../../../../constants/routes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    CompanyAdminLocationsQuery,
    LocationsForCompanyResponse,
    deleteLocationForCompany,
    fetchLocationsForCompany,
} from "../locationsApi"
import { Location } from "../locationsTypes"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useNavigate } from "react-router-dom"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"

const selectLocations = (data: LocationsForCompanyResponse) => data?.locations || []

export function OrganizationLocations() {
    const { companyId } = useCurrentCompany()

    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const LocationsResult = useApiQuery(fetchLocationsForCompany, {
        queryName: CompanyAdminLocationsQuery.LIST,
        dependencies: {
            companyId: companyId,
        },
        select: selectLocations,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(LocationsResult)

    const columns = useMemo<MRT_ColumnDef<Location>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("company-organization-locations-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-organization-locations-table-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const { mutate: callDeleteLocation } = useApiMutation(deleteLocationForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminLocationsQuery.LIST] })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteLocation({ locationId: confirmModalState.itemId })
        } else {
            throw new Error("Location id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteLocation])

    return (
        <>
            <LocalizedStrict id="company-organization-locations-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_LOCATIONS}
                    title="Locations"
                    rightElement={
                        <LocalizedStrict id="company-organization-locations-create-button">
                            <StandardButton onClick={() => navigate(COMPANY_ADMIN_ORGANIZATION_LOCATIONS_CREATE_ROUTE)}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-locations-title">
                            <Breadcrumb>Locations</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <Grid container direction="column">
                    <ApiQueryTable
                        columns={columns}
                        initialState={{ density: "spacious" }}
                        renderRowActionMenuItems={({ row, closeMenu }) => [
                            <LocalizedStrict id="company-organization-locations-table-edit">
                                <MenuItem
                                    onClick={() => {
                                        navigate(getEditLocationRoute(row.original.id))
                                    }}
                                    key={`edit-${row.original.id}`}
                                >
                                    Edit
                                </MenuItem>
                            </LocalizedStrict>,
                            <LocalizedStrict id="company-organization-locations-table-delete">
                                <MenuItem
                                    onClick={() => {
                                        setConfirmModalState({
                                            showConfirmModal: true,
                                            confirmModalText: `${row.original.name}`,
                                            itemId: row.original.id,
                                        })
                                        closeMenu()
                                    }}
                                    key={`delete-${row.original.id}`}
                                >
                                    Delete
                                </MenuItem>
                            </LocalizedStrict>,
                        ]}
                    />
                </Grid>
            </Grid>

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("company-organization-locations-table-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    open={confirmModalState.showConfirmModal}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                />
            )}
        </>
    )
}
